import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Id } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import { User } from "@/jenesys-types/jenesys-entities/config";

interface MembersData {
  members: Record<Id, User>;
  totalNumMembers: number;
  isLoading: boolean;
  error?: AppError;
  activeId: string | null;
  searchesFetched: string[];
}

export interface CompanyMembersState {
  companyMembers: MembersData;
  clientMembers: MembersData;
}

interface LoadCompanyMembersPayload {
  companyId: Id;
  search?: string;
}
export type LoadCompanyMembersAction = PayloadAction<LoadCompanyMembersPayload>;

interface LoadClientMembersPayload {
  clientId: Id;
  search?: string;
}
export type LoadClientMembersAction = PayloadAction<LoadClientMembersPayload>;

interface CompanyMembersSuccessPayload {
  members: User[];
  totalNumMembers: number;
  companyId: Id;
  search?: string;
}
export type CompanyMembersSuccessAction =
  PayloadAction<CompanyMembersSuccessPayload>;

interface ClientMembersSuccessPayload {
  members: User[];
  totalNumMembers: number;
  clientId: Id;
  search?: string;
}
export type ClientMembersSuccessAction =
  PayloadAction<ClientMembersSuccessPayload>;

interface CompanyMembersFailurePayload {
  error: AppError;
  companyId: Id;
}
export type CompanyMembersFailureAction =
  PayloadAction<CompanyMembersFailurePayload>;

interface ClientMembersFailurePayload {
  error: AppError;
  clientId: Id;
}
export type ClientMembersFailureAction =
  PayloadAction<ClientMembersFailurePayload>;

const name = "state/features/CompanyMembers";

const createMembersGroup = (): MembersData => ({
  members: {}, // user_id -> user
  totalNumMembers: 0,
  isLoading: false,
  error: undefined,
  activeId: null,
  searchesFetched: []
});

export const initialState: CompanyMembersState = {
  companyMembers: createMembersGroup(),
  clientMembers: createMembersGroup()
};

const CompanyMembersSlice = createSlice({
  name,
  initialState,
  reducers: {
    loadCompanyMembers: {
      reducer: (
        state: CompanyMembersState,
        action: LoadCompanyMembersAction
      ) => {
        state.companyMembers.isLoading = true;
        state.companyMembers.error = undefined;
        state.companyMembers.activeId = action.payload.companyId;
      },
      prepare: (companyId: Id, search?: string) => ({
        payload: { companyId, search }
      })
    },
    loadClientMembers: {
      reducer: (
        state: CompanyMembersState,
        action: LoadClientMembersAction
      ) => {
        state.clientMembers.isLoading = true;
        state.clientMembers.error = undefined;
        state.clientMembers.activeId = action.payload.clientId;
      },
      prepare: (clientId: Id, search?: string) => ({
        payload: { clientId, search }
      })
    },
    loadCompanyMembersSuccess: {
      reducer: (
        state: CompanyMembersState,
        action: CompanyMembersSuccessAction
      ) => {
        state.companyMembers.isLoading = false;
        state.companyMembers.members = Object.fromEntries([
          ...Object.entries(state.companyMembers.members),
          ...action.payload.members.map((member) => [member.id, member])
        ]);
        if (!action.payload.search) {
          state.companyMembers.totalNumMembers = action.payload.totalNumMembers;
        }
        state.companyMembers.error = undefined;
        if (action.payload.search) {
          if (
            !state.companyMembers.searchesFetched.includes(
              action.payload.search
            )
          ) {
            state.companyMembers.searchesFetched.push(action.payload.search);
          }
        }
      },
      prepare: (
        members: User[],
        totalNumMembers: number,
        companyId: Id,
        search?: string
      ) => ({
        payload: { members, totalNumMembers, companyId, search }
      })
    },
    loadClientMembersSuccess: {
      reducer: (
        state: CompanyMembersState,
        action: ClientMembersSuccessAction
      ) => {
        state.clientMembers.isLoading = false;
        state.clientMembers.members = Object.fromEntries([
          ...Object.entries(state.clientMembers.members),
          ...action.payload.members.map((member) => [member.id, member])
        ]);
        if (!action.payload.search) {
          state.clientMembers.totalNumMembers = action.payload.totalNumMembers;
        }
        state.clientMembers.error = undefined;
        if (action.payload.search) {
          if (
            !state.clientMembers.searchesFetched.includes(action.payload.search)
          ) {
            state.clientMembers.searchesFetched.push(action.payload.search);
          }
        }
      },
      prepare: (
        members: User[],
        totalNumMembers: number,
        clientId: Id,
        search?: string
      ) => ({
        payload: { members, totalNumMembers, clientId, search }
      })
    },
    loadCompanyMembersFailure: {
      reducer: (
        state: CompanyMembersState,
        action: CompanyMembersFailureAction
      ) => {
        state.companyMembers.isLoading = false;
        state.companyMembers.error = action.payload.error;
        state.companyMembers.members = {};
        state.companyMembers.totalNumMembers = 0;
      },
      prepare: (error: AppError, companyId: Id) => ({
        payload: { error, companyId }
      })
    },
    loadClientMembersFailure: {
      reducer: (
        state: CompanyMembersState,
        action: ClientMembersFailureAction
      ) => {
        state.clientMembers.isLoading = false;
        state.clientMembers.error = action.payload.error;
        state.clientMembers.members = {};
        state.clientMembers.totalNumMembers = 0;
      },
      prepare: (error: AppError, clientId: Id) => ({
        payload: { error, clientId }
      })
    },
    clearCompanyMembers: {
      reducer: (state: CompanyMembersState) => {
        state.companyMembers = createMembersGroup();
      },
      prepare: () => ({ payload: undefined })
    },
    clearClientMembers: {
      reducer: (state: CompanyMembersState) => {
        state.clientMembers = createMembersGroup();
      },
      prepare: () => ({ payload: undefined })
    },
    clearAllMembers: {
      reducer: (state: CompanyMembersState) => {
        state.companyMembers = createMembersGroup();
        state.clientMembers = createMembersGroup();
      },
      prepare: () => ({ payload: undefined })
    }
  }
});

export const {
  loadCompanyMembers,
  loadClientMembers,
  loadCompanyMembersSuccess,
  loadClientMembersSuccess,
  loadCompanyMembersFailure,
  loadClientMembersFailure,
  clearCompanyMembers,
  clearClientMembers,
  clearAllMembers
} = CompanyMembersSlice.actions;

// Selectors
export const selectCompanyMembersState = (state: {
  features: { CompanyMembers: CompanyMembersState };
}): CompanyMembersState => state.features.CompanyMembers;

export const selectCompanyMembers = createSelector(
  selectCompanyMembersState,
  (state) => Object.values(state.companyMembers.members)
);

export const selectClientMembers = createSelector(
  selectCompanyMembersState,
  (state) => Object.values(state.clientMembers.members)
);

export const selectAllMembers = createSelector(
  selectCompanyMembersState,
  (state) => [
    ...Object.values(state.clientMembers.members),
    ...Object.values(state.companyMembers.members)
  ]
);

export const selectCompanyMembersLoading = createSelector(
  selectCompanyMembersState,
  (state) => state.companyMembers.isLoading
);

export const selectClientMembersLoading = createSelector(
  selectCompanyMembersState,
  (state) => state.clientMembers.isLoading
);

export const selectMembersLoading = createSelector(
  selectCompanyMembersState,
  (state) => state.companyMembers.isLoading || state.clientMembers.isLoading
);

export const selectCompanyMembersError = createSelector(
  selectCompanyMembersState,
  (state) => state.companyMembers.error
);

export const selectClientMembersError = createSelector(
  selectCompanyMembersState,
  (state) => state.clientMembers.error
);

export const selectActiveCompanyId = createSelector(
  selectCompanyMembersState,
  (state) => state.companyMembers.activeId
);

export const selectActiveClientId = createSelector(
  selectCompanyMembersState,
  (state) => state.clientMembers.activeId
);

export const selectAllCompanyMembersLoaded = createSelector(
  selectCompanyMembersState,
  (state) =>
    Object.keys(state.companyMembers.members).length >=
    state.companyMembers.totalNumMembers
);

export const selectAllClientMembersLoaded = createSelector(
  selectCompanyMembersState,
  (state) =>
    Object.keys(state.clientMembers.members).length >=
    state.clientMembers.totalNumMembers
);

export const selectCompanyMembersSearchesFetched = createSelector(
  selectCompanyMembersState,
  (state) => new Set(state.companyMembers.searchesFetched)
);

export const selectClientMembersSearchesFetched = createSelector(
  selectCompanyMembersState,
  (state) => new Set(state.clientMembers.searchesFetched)
);

export default CompanyMembersSlice.reducer;
